// src/components/dashboard/CreditsCheckoutAsaas.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Heading,
    Text,
    VStack,
    HStack,
    FormControl,
    FormHelperText,
    Input,
    RadioGroup,
    Radio,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useToast,
    Divider,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    useBreakpointValue,
    useDisclosure,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription
} from '@chakra-ui/react';
import { FiCreditCard, FiDollarSign, FiFileText } from 'react-icons/fi';
import { supabase } from '../../config/supabaseClient';

// Importar componentes
import ProfileModal from './ProfileModal';
import CardCheckoutAsaas from './CardCheckoutAsaas';
import PixCheckoutAsaas from './PixCheckoutAsaas';
import BoletoCheckoutAsaas from './BoletoCheckoutAsaas';

// Componente principal do Checkout
const CreditsCheckoutAsaas = ({ isOpen, onClose, userId, onSuccess }) => {
    const [selectedPackage, setSelectedPackage] = useState('1');
    const [couponCode, setCouponCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [validCoupon, setValidCoupon] = useState(null);
    const [alreadyUsedCoupon, setAlreadyUsedCoupon] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(0); // 0: Cartão, 1: PIX, 2: Boleto
    const [profileData, setProfileData] = useState(null);
    const [profileError, setProfileError] = useState(false);

    // Modal para atualizar perfil
    const {
        isOpen: isProfileModalOpen,
        onOpen: onOpenProfileModal,
        onClose: onCloseProfileModal
    } = useDisclosure();

    const toast = useToast();
    const isMobile = useBreakpointValue({ base: true, md: false });

    // Definição dos pacotes
    const packages = [
        { id: '1', credits: 3, price: 15, description: 'Pacote Básico' },
        { id: '2', credits: 10, price: 44, description: 'Pacote Plus' },
        { id: '3', credits: 20, price: 85, description: 'Pacote Premium' },
    ];

    // Calcular preço com desconto - movido para fora do useEffect
    const calculatePrice = () => {
        const selectedPkg = packages.find(pkg => pkg.id === selectedPackage);
        if (!selectedPkg) return 0;
        const basePrice = selectedPkg.price;
        return validCoupon ? basePrice * 0.85 : basePrice; // 15% de desconto
    };

    // Verificar se o usuário tem CPF/CNPJ cadastrado
    useEffect(() => {
        const checkProfile = async () => {
            try {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('cpf_cnpj, phone, name')
                    .eq('id', userId)
                    .single();

                if (error) throw error;

                setProfileData(data);

                // Se não tiver CPF/CNPJ, abrir modal para preencher
                if (!data.cpf_cnpj) {
                    setProfileError(true);
                    onOpenProfileModal();
                } else {
                    setProfileError(false);
                }
            } catch (error) {
                console.error('Erro ao verificar perfil:', error);
                setProfileError(true);
            }
        };

        if (isOpen && userId) {
            checkProfile();
        }
    }, [isOpen, userId, onOpenProfileModal]);

    // Enviar evento begin_checkout quando o modal é aberto
    useEffect(() => {
        if (isOpen) {
            const selectedPkg = packages.find(pkg => pkg.id === selectedPackage);
            
            // Enviar evento begin_checkout ao Google Analytics
            if (window && window.gtag) {
                window.gtag('event', 'begin_checkout', {
                    currency: "BRL",
                    value: calculatePrice(),
                    items: [{
                        item_name: selectedPkg?.description || "Pacote de Créditos",
                        price: calculatePrice()
                    }]
                });
                //console.log('Evento begin_checkout enviado ao GA');
            }
        }
    }, [isOpen, selectedPackage, validCoupon]);

    // Função para atualizar o perfil
    const handleProfileUpdate = async () => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('cpf_cnpj, phone, name')
                .eq('id', userId)
                .single();

            if (error) throw error;

            setProfileData(data);

            if (data.cpf_cnpj) {
                setProfileError(false);
            } else {
                setProfileError(true);
            }
        } catch (error) {
            console.error('Erro ao atualizar perfil:', error);
            setProfileError(true);
        }
    };

    // Handler para mudança de método de pagamento - rastreia eventos
    const handlePaymentMethodChange = (index) => {
        setPaymentMethod(index);
        
        // Nomes dos métodos de pagamento
        const paymentTypes = ['credit_card', 'pix', 'boleto'];
        const currentPaymentType = paymentTypes[index];
        const selectedPkg = packages.find(pkg => pkg.id === selectedPackage);
        
        // Enviar evento ao Google Analytics
        if (window && window.gtag) {
            window.gtag('event', 'add_payment_info', {
                payment_type: currentPaymentType,
                currency: "BRL",
                value: calculatePrice(),
                items: [{
                    item_name: selectedPkg?.description || "Pacote de Créditos",
                    price: calculatePrice()
                }]
            });
            //console.log(`Evento add_payment_info (${currentPaymentType}) enviado ao GA`);
        }
    };

    // Verificar cupom
    const checkCoupon = async () => {
        if (!couponCode.trim()) return;

        setLoading(true);

        try {
            // Verificar se o usuário já usou algum cupom antes
            const { data: userProfile, error: userError } = await supabase
                .from('profiles')
                .select('cupom_used')
                .eq('id', userId)
                .single();

            if (userError) throw userError;

            if (userProfile.cupom_used) {
                setAlreadyUsedCoupon(true);
                setValidCoupon(null);

                toast({
                    title: 'Cupom não pode ser aplicado',
                    description: 'Você já utilizou um cupom promocional anteriormente.',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });

                return;
            }

            // Primeiro, verificar se não é o próprio cupom do usuário
            const { data: ownProfile, error: ownProfileError } = await supabase
                .from('profiles')
                .select('codigo_cupom')
                .eq('id', userId)
                .single();

            if (ownProfileError) throw ownProfileError;

            if (ownProfile.codigo_cupom === couponCode) {
                setValidCoupon(null);

                toast({
                    title: 'Cupom inválido',
                    description: 'Você não pode usar seu próprio código de cupom.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });

                return;
            }

            // Agora verificar se o cupom existe
            const { data, error } = await supabase.rpc('find_coupon_code', {
                p_coupon_code: couponCode
            });

            if (error) {
                throw error;
            }

            if (!data || data.length === 0) {
                setValidCoupon(null);

                toast({
                    title: 'Cupom inválido',
                    description: 'O código de cupom informado não é válido.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });

                return;
            }

            // Cupom válido
            setValidCoupon(data[0]);

            toast({
                title: 'Cupom aplicado!',
                description: 'Um desconto de 15% será aplicado na sua compra.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

        } catch (error) {
            setValidCoupon(null);

            toast({
                title: 'Erro ao verificar cupom',
                description: 'Ocorreu um erro ao verificar o cupom. Tente novamente.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent mx={{ base: 4, md: 0 }}>
                    <ModalHeader bgGradient="linear(to-r, brand.primary, brand.secondary)" color="white" borderTopRadius="md">
                        Comprar Créditos
                    </ModalHeader>
                    <ModalCloseButton color="white" />

                    <ModalBody py={6}>
                        {/* Alerta de CPF/CNPJ não cadastrado */}
                        {profileError && (
                            <Alert status="warning" mb={4}>
                                <AlertIcon />
                                <AlertTitle>Atenção!</AlertTitle>
                                <AlertDescription>
                                    Para realizar pagamentos, é necessário preencher seu CPF ou CNPJ.
                                    <Button
                                        ml={2}
                                        colorScheme="blue"
                                        size="sm"
                                        onClick={onOpenProfileModal}
                                    >
                                        Atualizar Perfil
                                    </Button>
                                </AlertDescription>
                            </Alert>
                        )}

                        <VStack spacing={6} align="stretch">
                            <Box>
                                <Heading size="md" mb={4} color="gray.700">
                                    Escolha seu pacote
                                </Heading>

                                <RadioGroup onChange={setSelectedPackage} value={selectedPackage}>
                                    <VStack spacing={4} align="stretch">
                                        {packages.map((pkg) => (
                                            <Box
                                                key={pkg.id}
                                                p={4}
                                                borderWidth="1px"
                                                borderRadius="md"
                                                borderColor={selectedPackage === pkg.id ? 'brand.primary' : 'gray.200'}
                                                _hover={{ borderColor: 'brand.primary' }}
                                            >
                                                <Radio value={pkg.id} colorScheme="blue">
                                                    <HStack justify="space-between" width="100%">
                                                        <Text fontWeight="medium">
                                                            {pkg.description} ({pkg.credits} créditos)
                                                        </Text>
                                                        <Text fontWeight="bold">
                                                            R$ {pkg.price.toFixed(2)}
                                                        </Text>
                                                    </HStack>
                                                </Radio>
                                            </Box>
                                        ))}
                                    </VStack>
                                </RadioGroup>
                            </Box>

                            <Box>
                                <Heading size="md" mb={4} color="gray.700">
                                    Cupom de desconto
                                </Heading>

                                <HStack>
                                    <FormControl>
                                        <Input
                                            placeholder="Código do cupom"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                            isDisabled={alreadyUsedCoupon || loading}
                                        />
                                        {alreadyUsedCoupon && (
                                            <FormHelperText color="orange.500">
                                                Você já utilizou um cupom promocional anteriormente.
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                    <Button
                                        onClick={checkCoupon}
                                        isLoading={loading}
                                        isDisabled={alreadyUsedCoupon || !couponCode.trim()}
                                    >
                                        Aplicar
                                    </Button>
                                </HStack>

                                {validCoupon && (
                                    <Box mt={2} p={2} bg="green.50" borderRadius="md">
                                        <Text color="green.600">
                                            Cupom válido! Desconto de 15% aplicado.
                                        </Text>
                                    </Box>
                                )}
                            </Box>

                            <Divider />

                            {/* Seleção de método de pagamento */}
                            <Box>
                                <Heading size="md" mb={4} color="gray.700">
                                    Forma de pagamento
                                </Heading>

                                <Tabs
                                    isFitted
                                    variant="enclosed"
                                    colorScheme="blue"
                                    index={paymentMethod}
                                    onChange={handlePaymentMethodChange} // Usando a função de rastreamento
                                >
                                    <TabList>
                                        <Tab>
                                            <HStack spacing={2}>
                                                <FiCreditCard />
                                                <Text display={{ base: "none", sm: "block" }}>Cartão</Text>
                                            </HStack>
                                        </Tab>
                                        <Tab>
                                            <HStack spacing={2}>
                                                <FiDollarSign />
                                                <Text display={{ base: "none", sm: "block" }}>PIX</Text>
                                            </HStack>
                                        </Tab>
                                        <Tab>
                                            <HStack spacing={2}>
                                                <FiFileText />
                                                <Text display={{ base: "none", sm: "block" }}>Boleto</Text>
                                            </HStack>
                                        </Tab>
                                    </TabList>

                                    <TabPanels mt={4}>
                                        <TabPanel p={0}>
                                            <CardCheckoutAsaas
                                                userId={userId}
                                                selectedPackage={selectedPackage}
                                                validCoupon={validCoupon}
                                                couponCode={couponCode}
                                                packages={packages}
                                                onSuccess={onSuccess}
                                                onClose={onClose}
                                            />
                                        </TabPanel>

                                        <TabPanel p={0}>
                                            <PixCheckoutAsaas
                                                userId={userId}
                                                selectedPackage={selectedPackage}
                                                validCoupon={validCoupon}
                                                couponCode={couponCode}
                                                packages={packages}
                                                onSuccess={onSuccess}
                                                onClose={onClose}
                                            />
                                        </TabPanel>

                                        <TabPanel p={0}>
                                            <BoletoCheckoutAsaas
                                                userId={userId}
                                                selectedPackage={selectedPackage}
                                                validCoupon={validCoupon}
                                                couponCode={couponCode}
                                                packages={packages}
                                                onSuccess={onSuccess}
                                                onClose={onClose}
                                            />
                                        </TabPanel>
                                    </TabPanels>
                                </Tabs>
                            </Box>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};
export default CreditsCheckoutAsaas;